import { AppErrorBoundary } from '@/components/app-error-boundary';
import type {
  CopilotMessage,
  CopilotSession,
  CopilotStatefulMessage,
  CopilotStatefulToolCall,
  CopilotToolCallStatus,
} from '@customerio/hydra-copilot';
import { ORPHANED_TOOL_CALL_ERROR } from '@customerio/hydra-copilot';
import { useCopilotApproval, useCopilotAvailableTools } from '@customerio/hydra-copilot/react';
import { Box, Button } from '@customerio/pluma-components/react';
import React, { useState } from 'react';
import {
  BashDetail,
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtSource,
  ChainOfThoughtSources,
  ChainOfThoughtStep,
  type StepStatus,
} from '../primitives/chain-of-thought';
import { extractFilename } from '../../helpers/file-utils';
import { dedupeReferences } from '../../utils/references';
import { getToolCallHeaderLabel, getToolCallLabel } from '../../utils/tool-call-labels';
import { AnimateMessage } from './animate-message';

function toolCallStepStatus(status: CopilotToolCallStatus): StepStatus {
  if (status === 'executing') return 'active';
  if (status === 'error') return 'error';
  return 'complete';
}

function combinedStatus(toolCalls: CopilotStatefulToolCall[]): CopilotToolCallStatus {
  if (toolCalls.some((toolCall) => toolCall.status === 'executing')) return 'executing';
  if (toolCalls.some((toolCall) => toolCall.status === 'error')) return 'error';
  return 'success';
}

function buildHeaderLabel(toolCalls: CopilotStatefulToolCall[]): string {
  if (toolCalls.length === 0) return '';

  // Prefer first executing → first errored → last tool call
  const representative =
    toolCalls.find((toolCall) => toolCall.status === 'executing') ??
    toolCalls.find((toolCall) => toolCall.status === 'error') ??
    toolCalls[toolCalls.length - 1];

  const label = getToolCallHeaderLabel(representative);
  const suffix = toolCalls.length > 1 ? ` (+${toolCalls.length - 1} more)` : '';
  return label + suffix;
}

/**
 * For approved bash tool calls, the approval step has the command but only an
 * `{ status: "approved" }` response. The actual execution result (stdout/stderr)
 * lives on a sibling tool call. This helper finds it by matching on the same
 * command string, falling back to any bash sibling with output.
 */
function getBashResponse(
  toolCall: CopilotStatefulToolCall,
): { stdout?: string; stderr?: string; exit_code?: number } | null {
  const result = toolCall.response?.result;
  if (result != null && typeof result === 'object' && ('stdout' in result || 'stderr' in result)) {
    return result;
  }
  return null;
}

type ToolCallStepProps = { toolCall: CopilotStatefulToolCall };

function ToolCallStep({ toolCall }: ToolCallStepProps) {
  const references = toolCall.response?.result?.references as Array<{ title?: string; url?: string }> | undefined;
  const uniqueReferences = dedupeReferences(references);
  const isFileOp = toolCall.name === 'read' || toolCall.name === 'write';
  const rawPath = typeof toolCall.args?.path === 'string' ? toolCall.args.path : undefined;
  const path = rawPath != null ? extractFilename(rawPath) : undefined;
  // The label (getToolCallLabel) already shows the friendly error text on error status.
  // Only surface server error as additional description when it's a real error, not the
  // orphaned-tool-call instruction string that is meant for the LLM, not the user.
  const serverError = typeof toolCall.response?.error === 'string' ? toolCall.response.error : undefined;
  const errorMessage =
    toolCall.status === 'error' && serverError != null && serverError !== ORPHANED_TOOL_CALL_ERROR
      ? serverError
      : undefined;

  return (
    <ChainOfThoughtStep
      label={getToolCallLabel(toolCall)}
      status={toolCallStepStatus(toolCall.status)}
      description={errorMessage}
      icon={toolCall.name === 'search_docs' ? 'search' : undefined}
      expandable={toolCall.name === 'bash' && typeof toolCall.args?.command === 'string'}
      badge={isFileOp && path != null ? <ChainOfThoughtSource>{path}</ChainOfThoughtSource> : undefined}
    >
      {toolCall.name === 'bash' && typeof toolCall.args?.command === 'string' ? (
        <BashDetail command={toolCall.args.command} response={getBashResponse(toolCall)} />
      ) : null}
      {toolCall.name === 'search_docs' && uniqueReferences.length > 0 ? (
        <ChainOfThoughtSources>
          {uniqueReferences.map((reference) => (
            <ChainOfThoughtSource key={reference.url ?? reference.title} href={reference.url}>
              {reference.title}
            </ChainOfThoughtSource>
          ))}
        </ChainOfThoughtSources>
      ) : null}
    </ChainOfThoughtStep>
  );
}

type StandardToolCallSegmentProps = { toolCalls: CopilotStatefulToolCall[] };

function StandardToolCallSegment({ toolCalls }: StandardToolCallSegmentProps) {
  const status = combinedStatus(toolCalls);
  const headerLabel = buildHeaderLabel(toolCalls);

  return (
    <AnimateMessage>
      <ChainOfThought isStreaming={status === 'executing'}>
        <ChainOfThoughtHeader status={toolCallStepStatus(status)}>{headerLabel}</ChainOfThoughtHeader>
        <ChainOfThoughtContent>
          {toolCalls.map((toolCall) => (
            <ToolCallStep key={toolCall.id} toolCall={toolCall} />
          ))}
        </ChainOfThoughtContent>
      </ChainOfThought>
    </AnimateMessage>
  );
}

/**
 * Fallback UI for a crashed tool render. Extracted into its own component
 * so it can use `useState` to guard the Continue button against double-clicks.
 */

type ToolCallErrorFallbackProps = { toolCall: CopilotStatefulToolCall; error: Error };

function ToolCallErrorFallback({ toolCall, error }: ToolCallErrorFallbackProps) {
  const [continued, setContinued] = useState(false);
  const canContinue = toolCall.status === 'executing' && toolCall.respond != null && !continued;

  return (
    <ChainOfThought>
      <ChainOfThoughtStep
        label={toolCall.name}
        status="error"
        description={`Something went wrong rendering this tool: ${error.message}`}
      />
      {canContinue ? (
        <Box px="100" pb="100">
          <Button
            variant="subtle"
            size="sm"
            onClick={() => {
              setContinued(true);
              toolCall.respond?.('The tool UI crashed and could not be displayed. Please continue without this tool.');
            }}
          >
            Continue
          </Button>
        </Box>
      ) : null}
    </ChainOfThought>
  );
}

/**
 * Error boundary for custom tool renders. When a render crash occurs,
 * shows a degraded ChainOfThought step with a Continue button for
 * human-in-the-loop tools so the conversation isn't permanently stuck.
 */

type ToolCallErrorBoundaryProps = {
  toolCall: CopilotStatefulToolCall;
  children: React.ReactNode;
};

function ToolCallErrorBoundary({ toolCall, children }: ToolCallErrorBoundaryProps) {
  return (
    <AppErrorBoundary fallbackRender={({ error }) => <ToolCallErrorFallback toolCall={toolCall} error={error} />}>
      {children}
    </AppErrorBoundary>
  );
}

/**
 * Builds a tool_response message that rejects one or more pending approval tool calls.
 */
function buildApprovalDenialMessage(approvals: Array<{ id: string; name: string }>, reason?: string): CopilotMessage {
  return {
    role: 'user',
    type: 'tool_response',
    tool_responses: approvals.map((approval) => ({
      tool_call_id: approval.id,
      name: approval.name,
      response: { result: { status: 'rejected' as const, reason }, error: null },
      finished: true,
    })),
    timestamp: Math.floor(Date.now() / 1000),
  };
}

type ToolCallsProps = {
  message: CopilotStatefulMessage & { type: 'tool_call' };
  session: CopilotSession;
};

export function ToolCalls({ message, session }: ToolCallsProps) {
  const tools = useCopilotAvailableTools(session);
  const ApprovalComponent = useCopilotApproval();

  const resolveToolRender = (toolCall: (typeof message.tool_calls)[0]) => {
    // Only use the approval renderer while the approval is still pending (executing).
    // Completed approvals fall through to standard rendering.
    if (toolCall.needs_approval === true && toolCall.status === 'executing' && ApprovalComponent != null) {
      return (props: { session: CopilotSession } & (typeof message.tool_calls)[0]) => {
        const sendApprovalResponse = (decision: { status: 'approved' | 'rejected'; reason?: string }) => {
          props.session.sendMessage([
            decision.status === 'approved'
              ? {
                  role: 'user',
                  type: 'tool_response',
                  tool_responses: [
                    {
                      tool_call_id: props.id,
                      name: props.name,
                      response: { result: decision, error: null },
                      finished: true,
                    },
                  ],
                  timestamp: Math.floor(Date.now() / 1000),
                }
              : buildApprovalDenialMessage([{ id: props.id, name: props.name }], decision.reason),
          ]);
        };

        return (
          <ApprovalComponent
            toolCall={props}
            session={props.session}
            onApprove={() => sendApprovalResponse({ status: 'approved' })}
            onDeny={(reason) => sendApprovalResponse({ status: 'rejected', reason })}
          />
        );
      };
    }

    return tools[toolCall.name]?.render;
  };
  // Build ordered segments that preserve the original tool call order.
  // Consecutive non-custom tools are grouped into a single collapsible block;
  // custom-rendered tools (approvals, etc.) are rendered individually in place.
  type CustomSegment = { type: 'custom'; toolCall: (typeof message.tool_calls)[0] };
  type StandardSegment = { type: 'standard'; toolCalls: (typeof message.tool_calls)[0][] };
  type Segment = CustomSegment | StandardSegment;

  const segments = message.tool_calls.reduce<Segment[]>((accumulator, toolCall) => {
    // Only use custom approval rendering while the approval is still pending.
    // Once completed (approved/rejected), render as a standard collapsible tool call.
    const isActiveApproval =
      toolCall.needs_approval === true && toolCall.status === 'executing' && ApprovalComponent != null;
    const hasCustomRender = isActiveApproval || resolveToolRender(toolCall) != null;

    if (hasCustomRender) {
      return [...accumulator, { type: 'custom', toolCall }];
    }

    const lastSegment = accumulator[accumulator.length - 1];
    if (lastSegment != null && lastSegment.type === 'standard') {
      return [...accumulator.slice(0, -1), { ...lastSegment, toolCalls: [...lastSegment.toolCalls, toolCall] }];
    }

    return [...accumulator, { type: 'standard', toolCalls: [toolCall] }];
  }, []);

  return (
    <>
      {segments.map((segment, segmentIndex) => {
        if (segment.type === 'custom') {
          const render = resolveToolRender(segment.toolCall);

          if (render == null) {
            return (
              <AnimateMessage key={segmentIndex}>
                <ChainOfThought>
                  <ChainOfThoughtStep label={segment.toolCall.name} status="error" />
                </ChainOfThought>
              </AnimateMessage>
            );
          }

          return (
            <AnimateMessage key={segmentIndex}>
              <ToolCallErrorBoundary toolCall={segment.toolCall}>
                <Box>{render({ ...segment.toolCall, session })}</Box>
              </ToolCallErrorBoundary>
            </AnimateMessage>
          );
        }

        return <StandardToolCallSegment key={segmentIndex} toolCalls={segment.toolCalls} />;
      })}
    </>
  );
}
